import { requestReauth } from '../../auth/ReauthDialog';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const DEV_WORKSPACE_ID = 'ws-dev-001';

// Token getter function - set by AuthContext to provide current valid token
let tokenGetter: (() => Promise<string | null>) | null = null;
let currentUserId: string | null = null;
let currentWorkspaceId: string | null = null;

/**
 * Set the token getter function (called from AuthContext)
 */
export function setAuthTokenGetter(getter: () => Promise<string | null>) {
  tokenGetter = getter;
}

/**
 * Get a valid access token from the configured auth provider.
 * Shared by external API services (PromptFactory, PersonaWiz, etc.) so they
 * use the same token as the main cxFab API client — the SPA stores tokens
 * via @codespeak11/cognito-passwordless (localStorage), NOT in Amplify's
 * session, so calling Amplify's fetchAuthSession() directly returns null.
 */
export async function getAuthToken(): Promise<string | null> {
  return tokenGetter ? tokenGetter() : null;
}

/**
 * Set the current user ID (called from AuthContext)
 */
export function setCurrentUserId(userId: string | null) {
  currentUserId = userId;
}

/**
 * Set the current workspace ID
 */
export function setCurrentWorkspaceId(workspaceId: string | null) {
  currentWorkspaceId = workspaceId;
}

/**
 * Get the current user ID for API calls
 */
export function getCurrentUserId(): string | null {
  return currentUserId;
}

/**
 * Get the current workspace ID for API calls
 */
export function getCurrentWorkspaceId(): string | null {
  // Fall back to dev workspace if not set
  return currentWorkspaceId ?? DEV_WORKSPACE_ID;
}

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
}

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  correlationId?: string;
}

export class ApiClientError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  public readonly details?: Record<string, unknown>;
  public readonly correlationId?: string;

  constructor(statusCode: number, error: ApiError) {
    super(error.message);
    this.name = 'ApiClientError';
    this.code = error.code;
    this.statusCode = statusCode;
    this.details = error.details;
    this.correlationId = error.correlationId;
  }
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { params, ...fetchOptions } = options;

  let url = `${API_BASE_URL}${endpoint}`;

  if (params) {
    const searchParams = new URLSearchParams(params);
    url = `${url}?${searchParams.toString()}`;
  }

  // Get auth token
  const token = tokenGetter ? await tokenGetter() : null;
  if (!token) {
    // Prompt the user to re-auth instead of silently failing. The
    // ReauthDialog listener at the app root will pop a "Sign in" dialog;
    // signing in goes through SSO so any still-valid central BrainDance
    // session re-auths transparently (no cross-site logout).
    requestReauth();
    throw new ApiClientError(401, {
      code: 'UNAUTHORIZED',
      message: 'Not authenticated',
    });
  }

  // Get workspace ID (falls back to dev workspace in dev mode)
  const workspaceId = getCurrentWorkspaceId();

  const doFetch = (authToken: string) =>
    fetch(url, {
      ...fetchOptions,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        ...(workspaceId && { 'X-Workspace-Id': workspaceId }),
        ...fetchOptions.headers,
      },
    });

  let response = await doFetch(token);

  // Per spa-auth-integration.md: on a fresh 401, retry ONCE with a
  // freshly-refreshed token before giving up. Covers the race where the
  // SDK's proactive refresh hadn't completed by the time the request fired.
  // Only the second failure should trigger the re-auth dialog.
  if (response.status === 401 && tokenGetter) {
    const refreshed = await tokenGetter();
    if (refreshed && refreshed !== token) {
      response = await doFetch(refreshed);
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      code: 'UNKNOWN_ERROR',
      message: 'An unexpected error occurred',
    }));
    // Still 401 after the retry above \u2014 refresh token is truly gone.
    // Prompt the user. NEVER call logout() here: that would wipe the
    // shared auth.braindance.net cookie and force OTP on every
    // BrainDance site.
    if (response.status === 401) {
      requestReauth();
    }
    throw new ApiClientError(response.status, errorData.error || errorData);
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  return JSON.parse(text) as T;
}

export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),
    
  post: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),
    
  put: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),
    
  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
};
